import type { Expression } from "../types";

/**
 * Layered SVG portrait specs (16 §6, 19 §2.5, §8.3).
 *
 * A portrait = base (head shape, skin) + hair + facial hair + glasses + clothing, with the
 * eyes / brows / mouth layers swapped per expression. Named cast members are hand-tuned below;
 * everyone else is assembled deterministically from a seed (their person id), so a generated
 * person always looks the same and belongs in the same style.
 *
 * The expression shown is the one the server sends: what the person chooses to show, never
 * their true state.
 */

export const EXPRESSIONS: readonly Expression[] = ["neutral", "pleased", "concerned", "tired", "irritated", "upset", "guarded"];

// --- parts -----------------------------------------------------------------------------------

export const SKIN_TONES = [
  { base: "#F3D6C1", shade: "#DDB49A" },
  { base: "#EBC3A3", shade: "#D2A27E" },
  { base: "#D7A57E", shade: "#BB8761" },
  { base: "#B98058", shade: "#9C6642" },
  { base: "#8E5B3C", shade: "#744630" },
  { base: "#5E3B27", shade: "#4A2C1C" },
] as const;

export const HAIR_COLOURS = {
  black: "#211B18",
  darkBrown: "#3B2A20",
  brown: "#6A4A32",
  auburn: "#8A4B2A",
  blonde: "#B8925A",
  saltPepper: "#76716B",
  grey: "#A3A09B",
  silver: "#CFCCC6",
} as const;
export type HairColour = keyof typeof HAIR_COLOURS;

export type HairStyle =
  | "short" | "sidePart" | "receding" | "cropped" | "curlyShort"
  | "bob" | "long" | "bun" | "pixie" | "shoulder" | "afro" | "bald" | "locs" | "hijab";
export type FacialHair = "none" | "stubble" | "beard" | "moustache";
export type Glasses = "none" | "round" | "rect";
export type Clothing = "suitTie" | "suitOpen" | "blazerBlouse" | "cardigan" | "shirtTie" | "knit";
export type Age = "young" | "mid" | "senior";
export type FaceShape = "oval" | "round" | "long";

export interface PortraitSpec {
  skin: number;
  faceShape: FaceShape;
  age: Age;
  hairStyle: HairStyle;
  hairColour: HairColour;
  facialHair: FacialHair;
  glasses: Glasses;
  clothing: Clothing;
  clothingColour: string;
  /** Shirt, blouse or top under the outer layer. */
  innerColour: string;
  /** Tie colour, where a tie is worn. */
  accentColour: string;
}

// --- deterministic generation -----------------------------------------------------------------

/** FNV-1a 32-bit hash of a string. */
export function hashSeed(seed: string): number {
  let h = 0x811c9dc5;
  for (let i = 0; i < seed.length; i++) {
    h ^= seed.charCodeAt(i);
    h = Math.imul(h, 0x01000193);
  }
  return h >>> 0;
}

/** mulberry32: small, fast, good enough for picking parts. */
export function seededRandom(seed: string): () => number {
  let a = hashSeed(seed);
  return () => {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

function pick<T>(rnd: () => number, items: readonly T[]): T {
  return items[Math.floor(rnd() * items.length) % items.length] as T;
}

const OUTER_COLOURS = ["#2F3A4A", "#3A3F4B", "#2D4A4A", "#4A3F3A", "#3B4A5C", "#232833", "#5B4E6A", "#4D5B4A"];
const INNER_COLOURS = ["#F2F4F7", "#E9E3DA", "#DCE4EC", "#EFE6D8", "#E5E9E1"];
const TIE_COLOURS = ["#7A2E35", "#33507A", "#5A6B3C", "#3E4A6B", "#6B4E5A"];

/** Clothing follows role and seniority (16 §6). */
export function clothingForGrade(grade: string, rnd: () => number, styleSet: "a" | "b"): Clothing {
  const g = grade.toLowerCase();
  if (g.includes("partner")) return styleSet === "a" ? pick(rnd, ["suitTie", "suitOpen"] as const) : "blazerBlouse";
  if (g === "associate" || g.includes("solicitor")) return styleSet === "a" ? pick(rnd, ["suitOpen", "suitTie"] as const) : "blazerBlouse";
  if (g === "trainee" || g.includes("paralegal")) return styleSet === "a" ? "shirtTie" : pick(rnd, ["blazerBlouse", "cardigan"] as const);
  return pick(rnd, ["cardigan", "knit", "suitOpen"] as const);
}

/**
 * Build a spec from a seed. Same seed and grade → same face, every time.
 * "Style set" only steers which hair/clothing parts go together; it is not shown or used anywhere else.
 */
export function generateSpec(seed: string, grade = "staff"): PortraitSpec {
  const rnd = seededRandom(seed);
  const styleSet = rnd() < 0.5 ? "a" : "b";
  const skin = Math.floor(rnd() * SKIN_TONES.length);
  const age = pick(rnd, ["young", "mid", "mid", "senior"] as const);
  const darkHairOnly = skin >= 3;
  const youngColours: HairColour[] = darkHairOnly ? ["black", "darkBrown"] : ["black", "darkBrown", "brown", "auburn", "blonde"];
  const hairColour: HairColour = age === "senior" ? pick(rnd, ["grey", "silver", "saltPepper"] as const)
    : age === "mid" && rnd() < 0.3 ? "saltPepper" : pick(rnd, youngColours);
  const hairStyle: HairStyle = styleSet === "a"
    ? pick(rnd, skin >= 4 ? ["cropped", "short", "bald", "curlyShort"] as const : age === "senior" ? ["receding", "short", "sidePart", "bald"] as const : ["short", "sidePart", "curlyShort", "cropped"] as const)
    : pick(rnd, skin >= 4 ? ["afro", "bun", "long", "pixie", "bob", "locs", "hijab"] as const : ["bob", "long", "bun", "pixie", "shoulder", "hijab"] as const);
  const facialHair: FacialHair = styleSet === "a" ? pick(rnd, ["none", "none", "stubble", "beard", "moustache"] as const) : "none";
  const clothing = clothingForGrade(grade, rnd, styleSet);
  const outer = pick(rnd, OUTER_COLOURS);
  return {
    skin,
    faceShape: pick(rnd, ["oval", "round", "long"] as const),
    age,
    hairStyle,
    hairColour,
    facialHair,
    glasses: rnd() < (age === "senior" ? 0.6 : 0.25) ? pick(rnd, ["round", "rect"] as const) : "none",
    clothing,
    clothingColour: clothing === "shirtTie" ? pick(rnd, INNER_COLOURS) : outer,
    innerColour: pick(rnd, INNER_COLOURS),
    accentColour: pick(rnd, TIE_COLOURS),
  };
}
